'use client'

import { useCallback, useEffect, useMemo, useState } from 'react'
import debounce from 'lodash.debounce'
import { usePathname, useRouter, useSearchParams } from 'next/navigation'
import qs from 'qs'

import { Brand, useFilterChange } from 'modules/products'
import { RadioItem } from 'shared/ui/RadioGroup'

import { createQueryString, FILTER_NAMES } from '../utils'

import { ClearButton } from './ClearButton'
import HorizontalExpendablePanel from './HorizontalExpendablePanel'
import { FiltersRadioGroup } from './styles'

interface ProducerFilterProps {
  brands: Brand[]
  isActive?: boolean
}

const ProducerFilter = ({ brands, isActive }: ProducerFilterProps) => {
  const [value, setValue] = useState<string | null>(null)
  const router = useRouter()
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const { setLastFilterChange } = useFilterChange()

  const handleSelect = useCallback(
    (filterValue: string | null) => {
      setValue(filterValue ?? 'all')
      setLastFilterChange(new Date())
      const newQueryString = createQueryString(
        new URLSearchParams(searchParams || ''),
        'brand_id',
        filterValue
      )

      router.replace(`${pathname}?${newQueryString}`)
    },
    [searchParams, pathname, router, setLastFilterChange]
  )

  const handleRadioChange = useMemo(
    () =>
      debounce((radioValue: string) => {
        handleSelect(radioValue === 'all' ? null : radioValue)
      }, 300),
    [handleSelect]
  )

  useEffect(() => {
    return () => {
      handleRadioChange.cancel()
    }
  }, [handleRadioChange])

  useEffect(() => {
    if (!searchParams) return
    const parsed = qs.parse(searchParams.toString())
    const queryValue =
      (parsed.filter as { brand_id?: string })?.brand_id || null

    const normalizedValue = queryValue ?? 'all'

    if (value !== normalizedValue) {
      setValue(normalizedValue)
    }
  }, [searchParams, value])

  return (
    <HorizontalExpendablePanel
      isActive={isActive}
      title={FILTER_NAMES.Producer}
    >
      {isActive && (
        <ClearButton onClick={() => handleSelect(null)}>
          Wyczyść filtr
        </ClearButton>
      )}
      <FiltersRadioGroup
        name='desktop-brands'
        selectedValue={value}
        onChange={handleRadioChange}
      >
        <RadioItem value='all'>Wszyscy</RadioItem>
        {brands?.map(brand => (
          <RadioItem key={brand.id} value={brand.id}>
            {brand.name} ({brand.productsCount})
          </RadioItem>
        ))}
      </FiltersRadioGroup>
    </HorizontalExpendablePanel>
  )
}

export default ProducerFilter
